import { HOV_SKILL_UNLOCK_HOV_CORE_THRESHOLD } from "@/features/hoverIdle/unlocks/hovTabUnlocks";

export type ResourceId = "hov" | "tim" | "fai" | "hovCore" | "mp" | "hg" | "ip";

export interface ResourceDefinition {
  readonly id: ResourceId;
  readonly label: string;
}

export type ResetLayerId = "mhov" | "nhov" | "ohov";
export type FeatureAvailabilityId = "exploration";

export type UnlockCondition =
  | { readonly type: "always" }
  | {
      readonly type: "resourceAtLeast";
      readonly resource: ResourceId;
      readonly amount: number;
    }
  | {
      readonly type: "resetCountAtLeast";
      readonly layer: ResetLayerId;
      readonly count: number;
    }
  | { readonly type: "skillPurchased"; readonly skillId: string }
  | { readonly type: "challengeCleared"; readonly challengeId: string }
  | {
      readonly type: "featureAvailable";
      readonly feature: FeatureAvailabilityId;
    }
  | { readonly type: "and"; readonly conditions: readonly UnlockCondition[] }
  | { readonly type: "or"; readonly conditions: readonly UnlockCondition[] }
  | { readonly type: "not"; readonly condition: UnlockCondition };

type ReadonlyIdCollection = ReadonlySet<string> | readonly string[];

export interface UnlockEvaluationContext {
  readonly resources?: Readonly<Partial<Record<ResourceId, number>>>;
  readonly resetCounts?: Readonly<Partial<Record<ResetLayerId, number>>>;
  readonly purchasedSkillIds?: ReadonlyIdCollection;
  readonly clearedChallengeIds?: ReadonlyIdCollection;
  readonly availableFeatures?: ReadonlyIdCollection;
}

export type ModifierTarget =
  | "hov.gainPerSecond"
  | "tim.gainPerSecond"
  | "fai.gain"
  | "hovCore.gain"
  | "mp.capacity"
  | "hg.multiplier"
  | "ip.gain"
  | "hov.upgradeCost"
  | "tim.upgradeCost"
  | "mhov.effect"
  | "nhov.effect"
  | "ohov.hovCoreGain"
  | "exploration.speed"
  | "challenge.reward";

export type ModifierOperation = "add" | "multiply";

export interface Modifier {
  readonly id: string;
  readonly target: ModifierTarget;
  readonly operation: ModifierOperation;
  readonly value: number;
  readonly condition?: UnlockCondition;
}

export interface ApplyNumericModifiersOptions {
  readonly baseValue: number;
  readonly target: ModifierTarget;
  readonly modifiers: readonly Modifier[];
  readonly context?: UnlockEvaluationContext;
}

export type HovTabId = "target" | "skill" | "amp" | "wall" | "exploration";

export type TabDefinitionStatus = "implemented" | "planned";

export interface TabDefinition<TId extends string = string> {
  readonly id: TId;
  readonly label: string;
  readonly unlockCondition: UnlockCondition;
  readonly status: TabDefinitionStatus;
}

export const RESOURCE_IDS = [
  "hov",
  "tim",
  "fai",
  "hovCore",
  "mp",
  "hg",
  "ip",
] as const satisfies readonly ResourceId[];

export const RESET_LAYER_IDS = [
  "mhov",
  "nhov",
  "ohov",
] as const satisfies readonly ResetLayerId[];

export const HOVER_IDLE_RESOURCE_DEFINITIONS: readonly ResourceDefinition[] = [
  { id: "hov", label: "Hov" },
  { id: "tim", label: "Tim" },
  { id: "fai", label: "Fai" },
  { id: "hovCore", label: "Hov Core" },
  { id: "mp", label: "MP" },
  { id: "hg", label: "HG" },
  { id: "ip", label: "IP" },
];

export const HOV_TABS: readonly TabDefinition<HovTabId>[] = [
  {
    id: "target",
    label: "Target",
    unlockCondition: { type: "always" },
    status: "implemented",
  },
  {
    id: "skill",
    label: "Skill",
    unlockCondition: {
      type: "resourceAtLeast",
      resource: "hovCore",
      amount: HOV_SKILL_UNLOCK_HOV_CORE_THRESHOLD,
    },
    status: "implemented",
  },
  {
    id: "amp",
    label: "Amp",
    unlockCondition: {
      type: "skillPurchased",
      skillId: "amplifier-unlock",
    },
    status: "implemented",
  },
  {
    id: "wall",
    label: "Wall",
    unlockCondition: { type: "skillPurchased", skillId: "wall-unlock" },
    status: "implemented",
  },
  {
    id: "exploration",
    label: "Exploration",
    unlockCondition: {
      type: "skillPurchased",
      skillId: "exploration-unlock",
    },
    status: "implemented",
  },
];

const RESOURCE_ID_SET = new Set<ResourceId>(RESOURCE_IDS);
const RESET_LAYER_ID_SET = new Set<ResetLayerId>(RESET_LAYER_IDS);
const RESOURCE_DEFINITIONS_BY_ID = new Map(
  HOVER_IDLE_RESOURCE_DEFINITIONS.map((definition) => [
    definition.id,
    definition,
  ]),
);

const EMPTY_UNLOCK_CONTEXT: UnlockEvaluationContext = {};

export function isResourceId(value: string): value is ResourceId {
  return RESOURCE_ID_SET.has(value as ResourceId);
}

export function isResetLayerId(value: string): value is ResetLayerId {
  return RESET_LAYER_ID_SET.has(value as ResetLayerId);
}

export function getResourceDefinition(id: ResourceId): ResourceDefinition {
  const definition = RESOURCE_DEFINITIONS_BY_ID.get(id);
  if (!definition) {
    throw new Error(`Unknown resource id: ${id}`);
  }
  return definition;
}

export function isUnlockConditionMet(
  condition: UnlockCondition,
  context: UnlockEvaluationContext = EMPTY_UNLOCK_CONTEXT,
): boolean {
  switch (condition.type) {
    case "always":
      return true;
    case "resourceAtLeast":
      assertFinite(condition.amount, "unlock condition amount");
      return getResourceAmount(context, condition.resource) >= condition.amount;
    case "resetCountAtLeast":
      assertFinite(condition.count, "unlock condition reset count");
      return getResetCount(context, condition.layer) >= condition.count;
    case "skillPurchased":
      return hasId(context.purchasedSkillIds, condition.skillId);
    case "challengeCleared":
      return hasId(context.clearedChallengeIds, condition.challengeId);
    case "featureAvailable":
      return hasId(context.availableFeatures, condition.feature);
    case "and":
      return condition.conditions.every((entry) =>
        isUnlockConditionMet(entry, context),
      );
    case "or":
      return condition.conditions.some((entry) =>
        isUnlockConditionMet(entry, context),
      );
    case "not":
      return !isUnlockConditionMet(condition.condition, context);
    default:
      return assertNever(condition);
  }
}

export function applyNumericModifiers(
  options: ApplyNumericModifiersOptions,
): number {
  assertFinite(options.baseValue, "modifier base value");
  const context = options.context ?? EMPTY_UNLOCK_CONTEXT;
  let addValue = 0;
  let multiplier = 1;

  for (const modifier of options.modifiers) {
    if (modifier.target !== options.target) continue;
    if (
      modifier.condition &&
      !isUnlockConditionMet(modifier.condition, context)
    ) {
      continue;
    }

    assertFinite(modifier.value, `modifier ${modifier.id} value`);
    switch (modifier.operation) {
      case "add":
        addValue += modifier.value;
        break;
      case "multiply":
        multiplier *= modifier.value;
        break;
      default:
        assertNever(modifier.operation);
    }
  }

  return (options.baseValue + addValue) * multiplier;
}

function getResourceAmount(
  context: UnlockEvaluationContext,
  resource: ResourceId,
): number {
  return context.resources?.[resource] ?? 0;
}

function getResetCount(
  context: UnlockEvaluationContext,
  layer: ResetLayerId,
): number {
  return context.resetCounts?.[layer] ?? 0;
}

function hasId(
  collection: ReadonlyIdCollection | undefined,
  id: string,
): boolean {
  if (!collection) return false;
  return isReadonlyStringArray(collection)
    ? collection.includes(id)
    : collection.has(id);
}

function isReadonlyStringArray(
  collection: ReadonlyIdCollection,
): collection is readonly string[] {
  return Array.isArray(collection);
}

function assertFinite(value: number, label: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unexpected hover idle definition value: ${String(value)}`);
}
